/**
 * appHygiene — Light-weight client-side cleanup + auto-update.
 *
 * Purpose
 * -------
 * Keep the app fluid over time on tablets / workstations that never close
 * their browser, without forcing the user to clear their cache manually.
 *
 * What it does
 * ------------
 * 1. On boot, prunes stale entries from localStorage (offline queued form
 *    drafts, cached lookups…) that are older than 30 days OR that look
 *    corrupted. Never touches auth tokens or user preferences.
 *
 * 2. Polls `/api/health/version` every 5 min. When the deployed build hash
 *    changes (new deploy), it unregisters any service worker, clears the
 *    Cache Storage API, and reloads the page automatically once the user
 *    is idle. No more "clear your browser cache" support tickets.
 *
 * The helpers are intentionally defensive : they swallow every exception
 * so a faulty storage key can never break the whole app.
 */

const STALE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const VERSION_POLL_MS = 5 * 60 * 1000;     // 5 min
const VERSION_KEY = '__lm_build_version';

// Keys that we MUST never touch even if they look old — session, prefs, etc.
const PROTECTED_KEY_PATTERNS = [
    /^user/i,
    /auth/i,
    /token/i,
    /preferences?/i,
    /settings?/i,
    /theme/i,
    /^lastseen/i,
    VERSION_KEY,
];

const isProtected = (key) => PROTECTED_KEY_PATTERNS.some(
    (p) => (typeof p === 'string' ? p === key : p.test(key))
);

/** Best-effort parse of a timestamp inside a stored value. */
function extractTimestamp(raw) {
    if (!raw || typeof raw !== 'string') return null;
    try {
        const obj = JSON.parse(raw);
        if (obj && typeof obj === 'object') {
            const ts = obj.timestamp || obj.ts || obj.updated_at || obj.created_at;
            if (typeof ts === 'number') return ts;
            if (typeof ts === 'string') {
                const d = Date.parse(ts);
                return isNaN(d) ? null : d;
            }
        }
    } catch {
        /* not JSON, skip */
    }
    return null;
}

/** Remove obviously stale localStorage entries. */
export function pruneStaleStorage() {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
        const now = Date.now();
        const keys = Object.keys(window.localStorage);
        for (const key of keys) {
            if (isProtected(key)) continue;
            const raw = window.localStorage.getItem(key);
            // Drop oversized leftovers (> 200 KB) — usually a broken cache.
            if (raw && raw.length > 200 * 1024) {
                window.localStorage.removeItem(key);
                continue;
            }
            const ts = extractTimestamp(raw);
            if (ts && now - ts > STALE_MS) {
                window.localStorage.removeItem(key);
            }
        }
    } catch {
        /* storage disabled in private mode — noop */
    }
}

/**
 * Ask the browser to drop any SW-controlled caches. Runs safely when no
 * service worker is registered (typical CRA setup).
 */
async function dropAllCaches() {
    try {
        if ('caches' in window) {
            const names = await caches.keys();
            await Promise.all(names.map((n) => caches.delete(n)));
        }
    } catch { /* noop */ }
    try {
        if ('serviceWorker' in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map((r) => r.unregister()));
        }
    } catch { /* noop */ }
}

/** Pulls the current deployed build hash from the server. */
async function fetchBuildVersion() {
    try {
        const res = await fetch('/api/health/version', {
            method: 'GET',
            credentials: 'include',
            cache: 'no-store',
            headers: { 'Cache-Control': 'no-cache' },
        });
        if (!res.ok) return null;
        const data = await res.json().catch(() => null);
        return data && (data.version || data.build || null);
    } catch {
        return null;
    }
}

function scheduleReload() {
    // Wait until the user is idle to avoid losing in-progress form data.
    const doReload = () => {
        try { window.location.reload(); } catch { /* noop */ }
    };
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        doReload();
    } else {
        // Reload in 10s, giving time to the user to see the toast & save.
        setTimeout(doReload, 10_000);
    }
}

/** Start periodic version polling. Returns a cleanup function. */
export function installAutoUpdate({ onUpdateAvailable } = {}) {
    if (typeof window === 'undefined') return () => {};

    let cancelled = false;

    const tick = async () => {
        if (cancelled) return;
        const current = await fetchBuildVersion();
        if (!current) return;
        const previous = window.localStorage.getItem(VERSION_KEY);
        if (!previous) {
            window.localStorage.setItem(VERSION_KEY, current);
            return;
        }
        if (previous !== current) {
            window.localStorage.setItem(VERSION_KEY, current);
            await dropAllCaches();
            if (typeof onUpdateAvailable === 'function') {
                try { onUpdateAvailable(); } catch { /* noop */ }
            }
            scheduleReload();
        }
    };

    // First check after 30s so it doesn't race with initial auth.
    const firstT = setTimeout(tick, 30_000);
    const intervalId = setInterval(tick, VERSION_POLL_MS);
    return () => {
        cancelled = true;
        clearTimeout(firstT);
        clearInterval(intervalId);
    };
}

/** One-shot hygiene pass: call this at app boot. */
export function runBootHygiene() {
    pruneStaleStorage();
}
